import { useState, useEffect, useRef } from 'react'
import type { JSX } from 'react'

import { useApolloClient } from '@apollo/client/react'

import { useSplitEnabled } from '@buffer-mono/features'
import { HoverCard, Tooltip } from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { getChannelName } from '~publish/helpers/channels'
import {
  SingleChannelSyncContent,
  SyncTriggerIcon,
} from '~publish/pages/Community/components/SyncPopover'
import type { SyncIconState } from '~publish/pages/Community/components/SyncPopover'
import { useCommunityConfig } from '~publish/pages/Community/CommunityConfig'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { useBackfillCompletion } from '../BackfillContext'
import { useBackfillLoadingBanner } from '../hooks/backfill/useBackfillLoadingBanner'
import { useForceCommentsBackfill } from '../hooks/backfill/useForceCommentsBackfill'
import {
  setBackfillLoadingCookie,
  clearBackfillLoadingCookie,
  isBackfillLoading,
} from '../util/backfillCookies'
import {
  getIsRealtimeSync,
  getTimeAgo,
  getNextSyncTimeRange,
  getMinutesSinceLastSync,
} from '../util/channelSyncHelpers'
import { getFormattedNextSyncTimeRange } from '../util/dateHelpers'

import syncPopoverStyles from '~publish/components/SyncPopover/syncPopover.module.css'

export const LastSyncActions_Channel = graphql(/* GraphQL */ `
  fragment LastSyncActions_Channel on Channel @_unmask {
    id
    service
    type
    allowedActions
  }
`)

/**
 * `comments` is nullable-optional on the codegen `Channel` this still receives
 * pre-producer-flip, so it can't live in the required fragment shape yet; it's
 * added back as an optional field for the sync timestamps this reads. Collapses
 * into the fragment once the producer flips (CORE-6384).
 */
type LastSyncActionsChannel = FragmentOf<typeof LastSyncActions_Channel> & {
  comments?: {
    lastCheckedAt?: string | null
    nextUpdateAt?: string | null
  } | null
}

export const LastSyncActions = ({
  channel,
}: {
  channel: LastSyncActionsChannel
}): JSX.Element => {
  const [open, setOpen] = useState(false)
  const [showSuccessIcon, setShowSuccessIcon] = useState(false)
  const successIconTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )
  /** Which completion timestamp we already started the 1-minute timeout for (avoids duplicate timers). */
  const handledCompletionAtRef = useRef<number | null>(null)
  const { config } = useCommunityConfig()
  const {
    getChannelCompletionTimestamp,
    clearChannelCompleted,
    markChannelInitiated,
    clearChannelInitiated,
  } = useBackfillCompletion()
  const organizationId = useOrganizationId()
  const { forceBackfill } = useForceCommentsBackfill()

  const completionTimestamp = channel?.id
    ? getChannelCompletionTimestamp(channel.id)
    : undefined

  // isLoading stays in sync with the banner: if the banner is showing (active backfill
  // or the brief completion window), the HoverCard is suppressed and the tooltip is shown instead.
  const { showBanner: isLoading } = useBackfillLoadingBanner({ channel })

  const { isEnabled: skipRateLimitFeatureEnabled } = useSplitEnabled(
    'geid-comments-skip-rate-limit-force-backfilling',
  )

  const client = useApolloClient()

  // Calculate time ago for the last sync text
  const timeAgo = getTimeAgo(channel.comments?.lastCheckedAt, new Date())

  // Calculate minutes since last sync for disabling the sync button
  const minutesSinceLastSync = getMinutesSinceLastSync(
    channel.comments?.lastCheckedAt,
    new Date(),
  )

  // Disable sync if last sync was less than 60 minutes ago
  const isSyncDisabled =
    minutesSinceLastSync !== null &&
    minutesSinceLastSync < 60 &&
    !skipRateLimitFeatureEnabled

  // Refetch channel data when HoverCard opens to get fresh lastCheckedAt
  const handleOpenChange = (newOpen: boolean): void => {
    if (newOpen) {
      void client.refetchQueries({ include: ['GetChannelInfoForComments'] })
    }
    setOpen(newOpen)
  }

  // Show success icon for 1 minute after we see a completion timestamp in context.
  // The loading-banner hook clears completion from context ~1s after the success banner; we must not
  // tie this to `completionTimestamp` staying defined (or effect cleanup would clear our timeout).
  // Instead: on first sight of a timestamp, start a single 1-minute timeout. Clear early only when
  // a new backfill starts (cookie) so we don't confuse "banner cleared context" with "user synced again".
  useEffect(() => {
    if (!channel?.id) return

    const channelId = channel.id

    if (isBackfillLoading(channelId)) {
      if (successIconTimeoutRef.current) {
        clearTimeout(successIconTimeoutRef.current)
        successIconTimeoutRef.current = null
      }
      handledCompletionAtRef.current = null
      setShowSuccessIcon(false)
      return
    }

    if (typeof completionTimestamp !== 'number') {
      return
    }

    if (handledCompletionAtRef.current === completionTimestamp) {
      return
    }

    handledCompletionAtRef.current = completionTimestamp
    setShowSuccessIcon(true)

    if (successIconTimeoutRef.current) {
      clearTimeout(successIconTimeoutRef.current)
    }

    successIconTimeoutRef.current = setTimeout(() => {
      setShowSuccessIcon(false)
      successIconTimeoutRef.current = null
      handledCompletionAtRef.current = null
    }, 60_000)
  }, [channel?.id, completionTimestamp])

  useEffect(() => {
    return (): void => {
      if (successIconTimeoutRef.current) {
        clearTimeout(successIconTimeoutRef.current)
        successIconTimeoutRef.current = null
      }
      handledCompletionAtRef.current = null
    }
  }, [channel?.id])

  // Only members who can manage comments may trigger a manual sync — the
  // backfill mutation's authz requires `manageComments`. View-only/no-access
  // members still see the last-synced info; they just don't get the button.
  const canManageComments = canPerform(channel, 'manageComments')

  // Check if channel uses realtime sync (webhook/firehose)
  const isRealtimeSync = getIsRealtimeSync(channel, config)

  // Use nextUpdateAt from the API for polling channels
  const nextSyncTimeRange = getNextSyncTimeRange(
    channel.comments?.nextUpdateAt,
    new Date(),
    getFormattedNextSyncTimeRange,
  )

  const handleSyncNow = async (): Promise<void> => {
    if (organizationId && channel?.id) {
      setOpen(false) // Close the HoverCard immediately

      // Drop the previous sync's success state. The effect below keys off the
      // completion timestamp, which the loading banner may already have
      // cleared, so it would not fire for this new sync on its own.
      if (successIconTimeoutRef.current) {
        clearTimeout(successIconTimeoutRef.current)
        successIconTimeoutRef.current = null
      }
      handledCompletionAtRef.current = null
      setShowSuccessIcon(false)

      // Mark channel as having initiated a forced backfill
      markChannelInitiated(channel.id)

      // Clear any previous completion tracking
      clearChannelCompleted(channel.id)

      // Set cookie optimistically to show banner immediately
      setBackfillLoadingCookie(channel.id)

      try {
        // Trigger backfill
        const result = await forceBackfill({
          channelIds: [channel.id],
          organizationId,
          userForced: true,
        })
        if (!result.success) {
          // Clear cookie and initiated state if backfill failed
          clearBackfillLoadingCookie(channel.id)
          clearChannelInitiated(channel.id)
        }
      } catch {
        // Clear cookie and initiated state if forceBackfill threw an exception
        clearBackfillLoadingCookie(channel.id)
        clearChannelInitiated(channel.id)
      }
    }
  }

  // Green is reserved for channels that receive engagement on their own, so a
  // polled channel stays neutral even right after a successful manual sync and
  // only swaps the cloud for a refresh once a sync is worth offering again.
  const iconState: SyncIconState = isLoading
    ? 'checking'
    : isRealtimeSync
      ? 'upToDate'
      : isSyncDisabled || showSuccessIcon
        ? 'synced'
        : 'syncAvailable'

  // Don't show HoverCard for non-realtime channels when loading
  const shouldShowHoverCard = !isLoading

  return (
    <HoverCard
      open={shouldShowHoverCard ? open : false}
      onOpenChange={shouldShowHoverCard ? handleOpenChange : undefined}
      openDelay="fast"
      closeDelay="fast"
    >
      <HoverCard.Trigger>
        {isLoading ? (
          <Tooltip content="Checking for new comments">
            <div
              // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- The loading icon is the live status for the asynchronous refresh action.
              role="status"
              aria-live="polite"
              aria-label="Checking for new comments"
            >
              <SyncTriggerIcon state={iconState} />
            </div>
          </Tooltip>
        ) : (
          <button
            type="button"
            className={syncPopoverStyles.trigger}
            aria-label="About sync status"
          >
            <SyncTriggerIcon state={iconState} />
          </button>
        )}
      </HoverCard.Trigger>
      <HoverCard.Content
        className={syncPopoverStyles.content}
        align="center"
        side="bottom"
      >
        <SingleChannelSyncContent
          isRealtimeSync={isRealtimeSync}
          timeAgo={timeAgo}
          nextSyncTimeRange={nextSyncTimeRange}
          supportsMentions={config.supportsMentions(channel)}
          platform={getChannelName(channel.service)}
          syncNow={
            !canManageComments
              ? 'hidden'
              : isSyncDisabled
                ? 'disabled'
                : 'enabled'
          }
          onSyncNow={handleSyncNow}
        />
      </HoverCard.Content>
    </HoverCard>
  )
}
